import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ComposedChart, Bar, Line, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Calendar, Filter, RefreshCw, Package, Archive, FileText, ChevronDown, Search, X, DollarSign, Award, User, Users, Layers, TrendingUp } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';

const BaoCaoDoanhSo = () => {
  // Helper function to get default date range (first day of month to today)
  const getDefaultDateRange = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      from: formatDate(firstDayOfMonth),
      to: formatDate(today)
    };
  };

  const defaultDates = getDefaultDateRange();

  // State Management
  const [phieuList, setPhieuList] = useState([]);
  const [chiTietList, setChiTietList] = useState([]);

  // Tab state
  const [activeTab, setActiveTab] = useState('customer'); // 'customer' or 'category'

  // Filter states for customer report
  const [filterDateFrom, setFilterDateFrom] = useState(defaultDates.from);
  const [filterDateTo, setFilterDateTo] = useState(defaultDates.to);
  const [selectedNhomHang, setSelectedNhomHang] = useState('ALL');
  const [searchNhomHang, setSearchNhomHang] = useState('');
  const [showNhomHangDropdown, setShowNhomHangDropdown] = useState(false);

  // New filter states
  const [selectedNguoiPhuTrach, setSelectedNguoiPhuTrach] = useState('ALL');
  const [searchNguoiPhuTrach, setSearchNguoiPhuTrach] = useState('');
  const [showNguoiPhuTrachDropdown, setShowNguoiPhuTrachDropdown] = useState(false);

  const [selectedKhachHang, setSelectedKhachHang] = useState('ALL');
  const [searchKhachHang, setSearchKhachHang] = useState('');
  const [showKhachHangDropdown, setShowKhachHangDropdown] = useState(false);

  const [selectedDoDay, setSelectedDoDay] = useState('ALL');
  const [searchDoDay, setSearchDoDay] = useState('');
  const [showDoDayDropdown, setShowDoDayDropdown] = useState(false);

  // Filter states for category report
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  const [isLoading, setIsLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [activeQuickFilter, setActiveQuickFilter] = useState('thisMonth'); // Track active quick filter

  // Refs
  const nhomHangDropdownRef = useRef(null);
  const nguoiPhuTrachDropdownRef = useRef(null);
  const khachHangDropdownRef = useRef(null);
  const doDayDropdownRef = useRef(null);

  // Report data states
  const [doanhSoData, setDoanhSoData] = useState([]);
  const [tongDoanhSo, setTongDoanhSo] = useState({ soKien: 0, soKhoi: 0, doanhSo: 0 });

  // Category report data states
  const [categoryData, setCategoryData] = useState([]);
  const [categoryTotal, setCategoryTotal] = useState({ soKhoi: 0, doanhSo: 0 });

  const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'];

  // Get unique values for filters
  const uniqueNhomHang = [...new Set(chiTietList.map(item => item['NHOM_HANG']).filter(Boolean))];

  const uniqueNguoiPhuTrach = [...new Set(phieuList.map(item => item['NGUOIPHUTRACH']).filter(Boolean))];

  const uniqueKhachHang = [...new Set(phieuList.map(item => item['NCC_KHACHHANG']).filter(Boolean))];

  const uniqueDoDay = [...new Set(chiTietList.map(item => item['DAY']).filter(Boolean))].sort((a, b) => {
    const numA = parseFloat(a);
    const numB = parseFloat(b);
    return numA - numB;
  });

  // Filter functions
  const filteredNhomHang = uniqueNhomHang.filter(nhomHang =>
    nhomHang.toLowerCase().includes(searchNhomHang.toLowerCase())
  );

  const filteredNguoiPhuTrach = uniqueNguoiPhuTrach.filter(nguoi =>
    nguoi.toLowerCase().includes(searchNguoiPhuTrach.toLowerCase())
  );

  const filteredKhachHang = uniqueKhachHang.filter(khach =>
    khach.toLowerCase().includes(searchKhachHang.toLowerCase())
  );

  const filteredDoDay = uniqueDoDay.filter(day =>
    day.toString().toLowerCase().includes(searchDoDay.toLowerCase())
  );

  // Get display text functions
  const getSelectedNhomHangText = () => {
    if (selectedNhomHang === 'ALL') return 'Tất cả nhóm hàng';
    return selectedNhomHang;
  };

  const getSelectedNguoiPhuTrachText = () => {
    if (selectedNguoiPhuTrach === 'ALL') return 'Tất cả người phụ trách';
    return selectedNguoiPhuTrach;
  };

  const getSelectedKhachHangText = () => {
    if (selectedKhachHang === 'ALL') return 'Tất cả khách hàng';
    return selectedKhachHang;
  };

  const getSelectedDoDayText = () => {
    if (selectedDoDay === 'ALL') return 'Tất cả độ dày';
    return `${selectedDoDay} mm`;
  };

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nhomHangDropdownRef.current && !nhomHangDropdownRef.current.contains(event.target)) {
        setShowNhomHangDropdown(false);
      }
      if (nguoiPhuTrachDropdownRef.current && !nguoiPhuTrachDropdownRef.current.contains(event.target)) {
        setShowNguoiPhuTrachDropdown(false);
      }
      if (khachHangDropdownRef.current && !khachHangDropdownRef.current.contains(event.target)) {
        setShowKhachHangDropdown(false);
      }
      if (doDayDropdownRef.current && !doDayDropdownRef.current.contains(event.target)) {
        setShowDoDayDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch data
  const fetchData = async (showToast = false) => {
    try {
      setIsLoading(true);
      const [phieuResponse, chiTietResponse] = await Promise.all([
        authUtils.apiRequestKHO('XUATNHAPKHO', 'Find', {}),
        authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Find', {})
      ]);

      setPhieuList(phieuResponse);
      setChiTietList(chiTietResponse);

      if (showToast) {
        toast.success('Tải dữ liệu thành công');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Lỗi khi tải dữ liệu');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate Doanh So Ban Hang with new filters
  const calculateDoanhSo = useCallback(() => {
    const fromDate = filterDateFrom ? new Date(filterDateFrom) : null;
    const toDate = filterDateTo ? new Date(filterDateTo) : null;
    if (toDate) toDate.setHours(23, 59, 59, 999);

    // Tạo map phiếu để join dữ liệu
    const phieuMap = {};
    phieuList.forEach(phieu => {
      phieuMap[phieu['SOPHIEU']] = phieu;
    });

    // Lọc dữ liệu xuất trong kỳ và join với bảng cha
    const dataXuatTrongKy = chiTietList
      .map(item => {
        const phieu = phieuMap[item['SOPHIEU']] || {};
        return {
          ...item,
          NCC_KHACHHANG: phieu['NCC_KHACHHANG'],
          NGUOIPHUTRACH: phieu['NGUOIPHUTRACH'],
          TONGTIEN: phieu['TONGTIEN'],
          DIENGIAI: phieu['DIENGIAI']
        };
      })
      .filter(item => {
        const itemDate = new Date(item['NGAY_NHAP_XUAT']);
        const nghiepVu = item['NGHIEP_VU'];

        const inDateRange = (!fromDate || itemDate >= fromDate) && (!toDate || itemDate <= toDate);
        const isXuat = nghiepVu === 'XUAT';
        const matchNhomHang = selectedNhomHang === 'ALL' || item['NHOM_HANG'] === selectedNhomHang;
        const matchNguoiPhuTrach = selectedNguoiPhuTrach === 'ALL' || item['NGUOIPHUTRACH'] === selectedNguoiPhuTrach;
        const matchKhachHang = selectedKhachHang === 'ALL' || item['NCC_KHACHHANG'] === selectedKhachHang;
        const matchDoDay = selectedDoDay === 'ALL' || item['DAY'] === selectedDoDay;

        return inDateRange && isXuat && matchNhomHang && matchNguoiPhuTrach && matchKhachHang && matchDoDay;
      });

    // Tính doanh số theo khách hàng
    const doanhSoMap = {};

    dataXuatTrongKy.forEach(item => {
      const khachHang = item['NCC_KHACHHANG'] || 'Khách hàng chưa xác định';
      const soKhoi = parseFloat(item['SO_KHOI']) || 0;
      const thanhTien = parseFloat(item['THANHTIEN']) || 0;

      if (!doanhSoMap[khachHang]) {
        doanhSoMap[khachHang] = {
          khachHang: khachHang,
          soKien: 0,
          soKhoi: 0,
          doanhSo: 0
        };
      }

      doanhSoMap[khachHang].soKien += 1;
      doanhSoMap[khachHang].soKhoi += soKhoi;
      doanhSoMap[khachHang].doanhSo += thanhTien;
    });

    // Tính tổng tất cả khách hàng
    const allDoanhSo = Object.values(doanhSoMap);
    const totalSoKien = allDoanhSo.reduce((sum, item) => sum + item.soKien, 0);
    const totalSoKhoi = allDoanhSo.reduce((sum, item) => sum + item.soKhoi, 0);
    const totalDoanhSo = allDoanhSo.reduce((sum, item) => sum + item.doanhSo, 0);

    setTongDoanhSo({ soKien: totalSoKien, soKhoi: totalSoKhoi, doanhSo: totalDoanhSo });

    // Sắp xếp và lấy top 10 cho biểu đồ và bảng
    const sortedDoanhSo = allDoanhSo
      .sort((a, b) => b.doanhSo - a.doanhSo)
      .slice(0, 10);

    setDoanhSoData(sortedDoanhSo);
  }, [chiTietList, phieuList, filterDateFrom, filterDateTo, selectedNhomHang, selectedNguoiPhuTrach, selectedKhachHang, selectedDoDay]);

  useEffect(() => {
    if (chiTietList.length > 0 && phieuList.length > 0) {
      calculateDoanhSo();
    }
  }, [calculateDoanhSo]);

  // Calculate Category Report (by DAY and TIEU_CHUAN) for all 12 months
  const calculateCategoryReport = useCallback(() => {
    // Filter data by year only, XUAT transactions
    const filteredData = chiTietList.filter(item => {
      const itemDate = new Date(item['NGAY_NHAP_XUAT']);
      const itemYear = itemDate.getFullYear();
      const nghiepVu = item['NGHIEP_VU'];

      return itemYear === filterYear && nghiepVu === 'XUAT';
    });

    // Group by DAY/TIEU_CHUAN and Month
    const categoryMonthMap = {};

    filteredData.forEach(item => {
      const itemDate = new Date(item['NGAY_NHAP_XUAT']);
      const month = itemDate.getMonth() + 1; // 1-12
      const day = item['DAY'] || '';
      const tieuChuan = item['TIEU_CHUAN'] || '';

      // Quy cách = DAY + TIEU_CHUAN (ví dụ: 18ABC, 22AB, 33BC)
      let categoryKey = `${day}${tieuChuan}`;

      // Nếu không có DAY hoặc TIEU_CHUAN, dùng giá trị có sẵn
      if (!day && !tieuChuan) {
        categoryKey = 'Không xác định';
      } else if (!day) {
        categoryKey = tieuChuan;
      } else if (!tieuChuan) {
        categoryKey = day;
      }

      if (!categoryMonthMap[categoryKey]) {
        categoryMonthMap[categoryKey] = {
          category: categoryKey,
          months: {}
        };
        // Initialize all 12 months
        for (let m = 1; m <= 12; m++) {
          categoryMonthMap[categoryKey].months[m] = {
            soKhoi: 0,
            doanhSo: 0
          };
        }
      }

      const soKhoi = parseFloat(item['SO_KHOI']) || 0;
      const thanhTien = parseFloat(item['THANHTIEN']) || 0;

      categoryMonthMap[categoryKey].months[month].soKhoi += soKhoi;
      categoryMonthMap[categoryKey].months[month].doanhSo += thanhTien;
    });

    // Calculate totals for each month
    const monthlyTotals = {};
    for (let m = 1; m <= 12; m++) {
      monthlyTotals[m] = { soKhoi: 0, doanhSo: 0 };
    }

    Object.values(categoryMonthMap).forEach(cat => {
      for (let m = 1; m <= 12; m++) {
        monthlyTotals[m].soKhoi += cat.months[m].soKhoi;
        monthlyTotals[m].doanhSo += cat.months[m].doanhSo;
      }
    });

    // Calculate percentages and group totals by DAY
    const dayGroupTotals = {}; // { month: { day: soKhoi } }

    // First pass: calculate totals by DAY for each month
    Object.values(categoryMonthMap).forEach(cat => {
      const dayMatch = cat.category.match(/^(\d+)/);
      const day = dayMatch ? dayMatch[1] : 'other';

      for (let m = 1; m <= 12; m++) {
        if (!dayGroupTotals[m]) dayGroupTotals[m] = {};
        if (!dayGroupTotals[m][day]) dayGroupTotals[m][day] = 0;
        dayGroupTotals[m][day] += cat.months[m].soKhoi;
      }
    });

    // Second pass: calculate percentages
    Object.values(categoryMonthMap).forEach(cat => {
      const dayMatch = cat.category.match(/^(\d+)/);
      const day = dayMatch ? dayMatch[1] : 'other';

      for (let m = 1; m <= 12; m++) {
        const total = monthlyTotals[m].soKhoi;
        const dayTotal = dayGroupTotals[m][day] || 0;

        cat.months[m].tyLe = total > 0 ? (cat.months[m].soKhoi / total * 100) : 0;
        cat.months[m].tyLeNhom = total > 0 ? (dayTotal / total * 100) : 0;
        cat.months[m].giaBanTrungBinh = cat.months[m].soKhoi > 0 ? (cat.months[m].doanhSo / cat.months[m].soKhoi) : 0;
      }
    });

    // Convert to array and sort
    const categoriesArray = Object.values(categoryMonthMap).sort((a, b) => {
      // Ưu tiên sắp xếp 18ABC và 18BC lên đầu
      const orderMap = { '18ABC': 1, '18BC': 2 };
      const orderA = orderMap[a.category] || 999;
      const orderB = orderMap[b.category] || 999;
      if (orderA !== orderB) return orderA - orderB;

      // Tách DAY ra để sắp xếp theo số
      const extractDay = (cat) => {
        const match = cat.match(/^(\d+)/);
        return match ? parseInt(match[1]) : 9999;
      };

      const dayA = extractDay(a.category);
      const dayB = extractDay(b.category);

      if (dayA !== dayB) return dayA - dayB;

      // Nếu DAY giống nhau, sắp xếp theo TIEU_CHUAN (ABC trước)
      return a.category.localeCompare(b.category);
    });

    setCategoryData(categoriesArray);
    setCategoryTotal(monthlyTotals);
  }, [chiTietList, filterYear]);

  useEffect(() => {
    if (chiTietList.length > 0) {
      calculateCategoryReport();
    }
  }, [calculateCategoryReport]);

  const formatNumber = (num) => {
    return new Intl.NumberFormat('vi-VN').format(Math.round(num));
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(num);
  };

  // Quick date filter functions
  const setQuickDateFilter = (type) => {
    const today = new Date();
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    let fromDate, toDate;

    switch (type) {
      case 'today':
        fromDate = toDate = today;
        break;
      case 'yesterday':
        fromDate = toDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
        break;
      case 'thisWeek':
        const firstDayOfWeek = new Date(today);
        firstDayOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday
        fromDate = firstDayOfWeek;
        toDate = today;
        break;
      case 'lastWeek':
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay()); // Last Sunday
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6); // Last Monday
        fromDate = lastWeekStart;
        toDate = lastWeekEnd;
        break;
      case 'thisMonth':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        toDate = today;
        break;
      case 'lastMonth':
        fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        toDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'thisQuarter':
        const currentQuarter = Math.floor(today.getMonth() / 3);
        fromDate = new Date(today.getFullYear(), currentQuarter * 3, 1);
        toDate = today;
        break;
      case 'lastQuarter':
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        const quarterYear = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const quarterMonth = lastQuarter < 0 ? 9 : lastQuarter * 3;
        fromDate = new Date(quarterYear, quarterMonth, 1);
        toDate = new Date(quarterYear, quarterMonth + 3, 0);
        break;
      case 'thisYear':
        fromDate = new Date(today.getFullYear(), 0, 1);
        toDate = today;
        break;
      case 'lastYear':
        fromDate = new Date(today.getFullYear() - 1, 0, 1);
        toDate = new Date(today.getFullYear() - 1, 11, 31);
        break;
      default:
        return;
    }

    setFilterDateFrom(formatDate(fromDate));
    setFilterDateTo(formatDate(toDate));
    setActiveQuickFilter(type);
    toast.success(`Đã chọn: ${getQuickFilterLabel(type)}`);
  };

  const getQuickFilterLabel = (type) => {
    const labels = {
      today: 'Hôm nay',
      yesterday: 'Hôm qua',
      thisWeek: 'Tuần này',
      lastWeek: 'Tuần trước',
      thisMonth: 'Tháng này',
      lastMonth: 'Tháng trước',
      thisQuarter: 'Quý này',
      lastQuarter: 'Quý trước',
      thisYear: 'Năm nay',
      lastYear: 'Năm trước'
    };
    return labels[type] || '';
  };

  // Clear all filters function
  const clearAllFilters = () => {
    const defaultDates = getDefaultDateRange();
    setFilterDateFrom(defaultDates.from);
    setFilterDateTo(defaultDates.to);
    setSelectedNhomHang('ALL');
    setSelectedNguoiPhuTrach('ALL');
    setSelectedKhachHang('ALL');
    setSelectedDoDay('ALL');
    setSearchNhomHang('');
    setSearchNguoiPhuTrach('');
    setSearchKhachHang('');
    setSearchDoDay('');
    setActiveQuickFilter('thisMonth'); // Reset to default
    toast.info('Đã xóa tất cả bộ lọc');
  };

  // Dropdown component for reusability
  const DropdownFilter = ({
    label,
    icon: Icon,
    selectedValue,
    setSelectedValue,
    searchValue,
    setSearchValue,
    showDropdown,
    setShowDropdown,
    dropdownRef,
    filteredOptions,
    getDisplayText,
    placeholder,
    allText
  }) => (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
        <Icon className="w-3.5 h-3.5" />
        {label}:
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm bg-white text-left flex items-center justify-between hover:border-emerald-400 transition-colors"
        >
          <span className={selectedValue === 'ALL' ? 'text-gray-500' : 'text-gray-900'}>
            {getDisplayText()}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showDropdown ? 'transform rotate-180' : ''}`} />
        </button>

        {showDropdown && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
            {/* Search box */}
            <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={placeholder}
                  className="w-full pl-8 pr-8 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  onClick={(e) => e.stopPropagation()}
                />
                {searchValue && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchValue('');
                    }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Options list */}
            <div className="max-h-60 overflow-y-auto">
              <button
                onClick={() => {
                  setSelectedValue('ALL');
                  setShowDropdown(false);
                  setSearchValue('');
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-emerald-50 transition-colors ${selectedValue === 'ALL' ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-gray-700'
                  }`}
              >
                {allText}
              </button>

              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedValue(option);
                      setShowDropdown(false);
                      setSearchValue('');
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-emerald-50 transition-colors ${selectedValue === option ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-gray-700'
                      }`}
                  >
                    {label === 'Độ dày' ? `${option} mm` : option}
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-center text-sm text-gray-500">
                  Không tìm thấy kết quả phù hợp
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Clear button */}
      {selectedValue !== 'ALL' && (
        <button
          onClick={() => {
            setSelectedValue('ALL');
            setSearchValue('');
          }}
          className="absolute right-8 top-[30px] text-gray-400 hover:text-gray-600"
          title="Xóa lựa chọn"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  return (
    <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-3 md:p-4 mb-4 border border-gray-100">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-3 gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-md">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-800">Báo Cáo Doanh Số Bán Hàng</h1>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5">Thống kê doanh số theo khách hàng</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => fetchData(true)}
                disabled={isLoading}
                className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm disabled:opacity-50"
                title="Tải lại dữ liệu"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Làm mới</span>
              </button>
              {activeTab === 'customer' && (
                <button
                  onClick={() => setShowFilter(!showFilter)}
                  className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm ${showFilter
                      ? 'text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  <Filter className="w-4 h-4" />
                  <span className="hidden sm:inline">{showFilter ? 'Ẩn bộ lọc' : 'Hiện bộ lọc'}</span>
                </button>
              )}
            </div>
          </div>

          {/* Filters - Only show for customer report tab */}
          {showFilter && activeTab === 'customer' && (
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                  <Filter className="w-3.5 h-3.5" />
                  Bộ lọc báo cáo:
                </h3>
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1 hover:underline"
                >
                  <X className="w-3.5 h-3.5" />
                  Xóa tất cả bộ lọc
                </button>
              </div>

              {/* Quick Date Filters */}
              <div className="mb-3 pb-3 border-b border-gray-200">
                <label className="block text-xs font-medium text-gray-700 mb-2">Chọn nhanh:</label>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setQuickDateFilter('today')}
                    className={`px-2.5 py-1 text-xs border rounded transition-colors ${activeQuickFilter === 'today'
                        ? 'bg-blue-500 border-blue-600 text-white font-semibold'
                        : 'bg-white border-gray-300 hover:bg-blue-50 hover:border-blue-400'
                      }`}
                  >
                    Hôm nay
                  </button>
                  <button
                    onClick={() => setQuickDateFilter('yesterday')}
                    className={`px-2.5 py-1 text-xs border rounded transition-colors ${activeQuickFilter === 'yesterday'
                        ? 'bg-blue-500 border-blue-600 text-white font-semibold'
                        : 'bg-white border-gray-300 hover:bg-blue-50 hover:border-blue-400'
                      }`}
                  >
                    Hôm qua
                  </button>
                  <button
                    onClick={() => setQuickDateFilter('thisWeek')}
                    className={`px-2.5 py-1 text-xs border rounded transition-colors ${activeQuickFilter === 'thisWeek'
                        ? 'bg-blue-500 border-blue-600 text-white font-semibold'
                        : 'bg-white border-gray-300 hover:bg-blue-50 hover:border-blue-400'
                      }`}
                  >
                    Tuần này
                  </button>
                  <button
                    onClick={() => setQuickDateFilter('lastWeek')}
                    className={`px-2.5 py-1 text-xs border rounded transition-colors ${activeQuickFilter === 'lastWeek'
                        ? 'bg-blue-500 border-blue-600 text-white font-semibold'
                        : 'bg-white border-gray-300 hover:bg-blue-50 hover:border-blue-400'
                      }`}
                  >
                    Tuần trước
                  </button>
                  <button
                    onClick={() => setQuickDateFilter('thisMonth')}
                    className={`px-2.5 py-1 text-xs border rounded transition-colors ${activeQuickFilter === 'thisMonth'
                        ? 'bg-emerald-600 border-emerald-700 text-white font-semibold'
                        : 'bg-white border-gray-300 hover:bg-emerald-50 hover:border-emerald-400'
                      }`}
                  >
                    Tháng này
                  </button>
                  <button
                    onClick={() => setQuickDateFilter('lastMonth')}
                    className={`px-2.5 py-1 text-xs border rounded transition-colors ${activeQuickFilter === 'lastMonth'
                        ? 'bg-emerald-600 border-emerald-700 text-white font-semibold'
                        : 'bg-white border-gray-300 hover:bg-emerald-50 hover:border-emerald-400'
                      }`}
                  >
                    Tháng trước
                  </button>
                  <button
                    onClick={() => setQuickDateFilter('thisQuarter')}
                    className={`px-2.5 py-1 text-xs border rounded transition-colors ${activeQuickFilter === 'thisQuarter'
                        ? 'bg-purple-600 border-purple-700 text-white font-semibold'
                        : 'bg-white border-gray-300 hover:bg-purple-50 hover:border-purple-400'
                      }`}
                  >
                    Quý này
                  </button>
                  <button
                    onClick={() => setQuickDateFilter('lastQuarter')}
                    className={`px-2.5 py-1 text-xs border rounded transition-colors ${activeQuickFilter === 'lastQuarter'
                        ? 'bg-purple-600 border-purple-700 text-white font-semibold'
                        : 'bg-white border-gray-300 hover:bg-purple-50 hover:border-purple-400'
                      }`}
                  >
                    Quý trước
                  </button>
                  <button
                    onClick={() => setQuickDateFilter('thisYear')}
                    className={`px-2.5 py-1 text-xs border rounded transition-colors ${activeQuickFilter === 'thisYear'
                        ? 'bg-orange-600 border-orange-700 text-white font-semibold'
                        : 'bg-white border-gray-300 hover:bg-orange-50 hover:border-orange-400'
                      }`}
                  >
                    Năm nay
                  </button>
                  <button
                    onClick={() => setQuickDateFilter('lastYear')}
                    className={`px-2.5 py-1 text-xs border rounded transition-colors ${activeQuickFilter === 'lastYear'
                        ? 'bg-orange-600 border-orange-700 text-white font-semibold'
                        : 'bg-white border-gray-300 hover:bg-orange-50 hover:border-orange-400'
                      }`}
                  >
                    Năm trước
                  </button>
                </div>
              </div>

              {/* Row 1: Date filters + Customer filter */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5 mb-2.5">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Từ ngày:
                  </label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => {
                      setFilterDateFrom(e.target.value);
                      setActiveQuickFilter(null); // Clear active filter when manually changed
                    }}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Đến ngày:
                  </label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => {
                      setFilterDateTo(e.target.value);
                      setActiveQuickFilter(null); // Clear active filter when manually changed
                    }}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm bg-white"
                  />
                </div>

                <div className="md:col-span-2">
                  <DropdownFilter
                    label="Khách hàng"
                    icon={Users}
                    selectedValue={selectedKhachHang}
                    setSelectedValue={setSelectedKhachHang}
                    searchValue={searchKhachHang}
                    setSearchValue={setSearchKhachHang}
                    showDropdown={showKhachHangDropdown}
                    setShowDropdown={setShowKhachHangDropdown}
                    dropdownRef={khachHangDropdownRef}
                    filteredOptions={filteredKhachHang}
                    getDisplayText={getSelectedKhachHangText}
                    placeholder="Tìm kiếm khách hàng..."
                    allText="Tất cả khách hàng"
                  />
                </div>
              </div>

              {/* Row 2: Other 3 filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                <DropdownFilter
                  label="Nhóm hàng"
                  icon={Package}
                  selectedValue={selectedNhomHang}
                  setSelectedValue={setSelectedNhomHang}
                  searchValue={searchNhomHang}
                  setSearchValue={setSearchNhomHang}
                  showDropdown={showNhomHangDropdown}
                  setShowDropdown={setShowNhomHangDropdown}
                  dropdownRef={nhomHangDropdownRef}
                  filteredOptions={filteredNhomHang}
                  getDisplayText={getSelectedNhomHangText}
                  placeholder="Tìm kiếm nhóm hàng..."
                  allText="Tất cả nhóm hàng"
                />

                <DropdownFilter
                  label="Người phụ trách"
                  icon={User}
                  selectedValue={selectedNguoiPhuTrach}
                  setSelectedValue={setSelectedNguoiPhuTrach}
                  searchValue={searchNguoiPhuTrach}
                  setSearchValue={setSearchNguoiPhuTrach}
                  showDropdown={showNguoiPhuTrachDropdown}
                  setShowDropdown={setShowNguoiPhuTrachDropdown}
                  dropdownRef={nguoiPhuTrachDropdownRef}
                  filteredOptions={filteredNguoiPhuTrach}
                  getDisplayText={getSelectedNguoiPhuTrachText}
                  placeholder="Tìm kiếm người phụ trách..."
                  allText="Tất cả người phụ trách"
                />

                <DropdownFilter
                  label="Độ dày"
                  icon={Layers}
                  selectedValue={selectedDoDay}
                  setSelectedValue={setSelectedDoDay}
                  searchValue={searchDoDay}
                  setSearchValue={setSearchDoDay}
                  showDropdown={showDoDayDropdown}
                  setShowDropdown={setShowDoDayDropdown}
                  dropdownRef={doDayDropdownRef}
                  filteredOptions={filteredDoDay}
                  getDisplayText={getSelectedDoDayText}
                  placeholder="Tìm kiếm độ dày..."
                  allText="Tất cả độ dày"
                />
              </div>

              {/* Active filters display */}
              {(selectedNhomHang !== 'ALL' || selectedNguoiPhuTrach !== 'ALL' || selectedKhachHang !== 'ALL' || selectedDoDay !== 'ALL') && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-2">Đang lọc theo:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedNhomHang !== 'ALL' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                        <Package className="w-3 h-3" />
                        {selectedNhomHang}
                        <button onClick={() => setSelectedNhomHang('ALL')} className="ml-1 hover:text-emerald-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    {selectedNguoiPhuTrach !== 'ALL' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                        <User className="w-3 h-3" />
                        {selectedNguoiPhuTrach}
                        <button onClick={() => setSelectedNguoiPhuTrach('ALL')} className="ml-1 hover:text-blue-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    {selectedKhachHang !== 'ALL' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                        <Users className="w-3 h-3" />
                        {selectedKhachHang}
                        <button onClick={() => setSelectedKhachHang('ALL')} className="ml-1 hover:text-purple-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    {selectedDoDay !== 'ALL' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                        <Layers className="w-3 h-3" />
                        {selectedDoDay} mm
                        <button onClick={() => setSelectedDoDay('ALL')} className="ml-1 hover:text-orange-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border border-gray-100">
          <div className="flex gap-2 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('customer')}
              className={`px-4 py-2 font-semibold text-sm transition-all ${activeTab === 'customer'
                  ? 'text-emerald-600 border-b-2 border-emerald-600'
                  : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Báo cáo theo khách hàng
              </div>
            </button>
            <button
              onClick={() => setActiveTab('category')}
              className={`px-4 py-2 font-semibold text-sm transition-all ${activeTab === 'category'
                  ? 'text-emerald-600 border-b-2 border-emerald-600'
                  : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Báo cáo theo loại hàng
              </div>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {activeTab === 'customer' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-emerald-700">TỔNG DOANH SỐ</h3>
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="text-2xl font-bold text-emerald-900">
                  {formatCurrency(tongDoanhSo.doanhSo)}
                </div>
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-cyan-700">TỔNG SỐ KIỆN</h3>
                  <Package className="w-5 h-5 text-cyan-600" />
                </div>
                <div className="text-2xl font-bold text-cyan-900">
                  {formatNumber(tongDoanhSo.soKien)}
                </div>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-indigo-700">TỔNG KHỐI LƯỢNG</h3>
                  <Archive className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="text-2xl font-bold text-indigo-900">
                  {tongDoanhSo.soKhoi.toFixed(3)} m³
                </div>
              </div>
            </div>

            {/* Chart Section */}
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100 mb-4">
              <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Biểu Đồ Doanh Số Top 10 Khách Hàng
              </h2>

              <div className="bg-gray-50 p-4 rounded-lg">
                <ResponsiveContainer width="100%" height={500}>
                  <ComposedChart
                    data={doanhSoData.map((item, index) => ({
                      name: item.khachHang,
                      fullName: item.khachHang,
                      doanhSo: item.doanhSo,
                      index: index + 1
                    }))}
                    margin={{ top: 30, right: 30, left: 20, bottom: 120 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      fontSize={11}
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      interval={0}
                      tick={({ x, y, payload }) => {
                        const words = payload.value.split(' ');
                        const lines = [];
                        let currentLine = '';

                        words.forEach(word => {
                          if ((currentLine + word).length <= 20) {
                            currentLine += (currentLine ? ' ' : '') + word;
                          } else {
                            if (currentLine) lines.push(currentLine);
                            currentLine = word;
                          }
                        });
                        if (currentLine) lines.push(currentLine);

                        return (
                          <g transform={`translate(${x},${y})`}>
                            {lines.map((line, index) => (
                              <text
                                key={index}
                                x={0}
                                y={0}
                                dy={16 + index * 12}
                                textAnchor="end"
                                fill="#666"
                                transform="rotate(-45)"
                                fontSize={11}
                              >
                                {line}
                              </text>
                            ))}
                          </g>
                        );
                      }}
                    />
                    <YAxis
                      fontSize={10}
                      tickFormatter={(value) => {
                        if (value >= 1000000000) return `${(value / 1000000000).toFixed(1)}B`;
                        if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                        if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
                        return value;
                      }}
                      label={{ value: 'Doanh số (VNĐ)', angle: -90, position: 'insideLeft', fontSize: 11 }}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                              <p className="text-xs font-semibold text-gray-800">{payload[0].payload.fullName}</p>
                              <p className="text-xs text-emerald-600 font-bold mt-1">
                                {formatCurrency(payload[0].value)}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar
                      dataKey="doanhSo"
                      fill="#10b981"
                      radius={[8, 8, 0, 0]}
                      label={{
                        position: 'top',
                        fontSize: 10,
                        fontWeight: 'bold',
                        fill: '#374151',
                        formatter: (value) => {
                          if (value >= 1000000000) return `${(value / 1000000000).toFixed(1)}B`;
                          if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                          if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
                          return value;
                        }
                      }}
                    >
                      {doanhSoData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                    <Line
                      type="monotone"
                      dataKey="doanhSo"
                      stroke="#059669"
                      strokeWidth={3}
                      dot={{ fill: '#059669', r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Table Section */}
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
              <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" />
                Chi Tiết Top 10 Khách Hàng Có Doanh Số Cao Nhất
              </h2>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-emerald-50 to-emerald-100">
                    <tr>
                      <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Top</th>
                      <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase">Khách hàng</th>
                      <th className="px-3 py-3 text-center text-xs font-bold text-emerald-700 uppercase">Số kiện</th>
                      <th className="px-3 py-3 text-center text-xs font-bold text-emerald-700 uppercase">Khối lượng (m³)</th>
                      <th className="px-3 py-3 text-right text-xs font-bold text-emerald-700 uppercase">Doanh số</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {doanhSoData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${index === 0 ? 'bg-yellow-400 text-yellow-900' :
                              index === 1 ? 'bg-gray-300 text-gray-900' :
                                index === 2 ? 'bg-orange-400 text-orange-900' :
                                  'bg-emerald-100 text-emerald-800'
                            }`}>
                            {index + 1}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">{item.khachHang}</td>
                        <td className="px-3 py-2 text-sm text-center text-emerald-600 font-semibold">{formatNumber(item.soKien)}</td>
                        <td className="px-3 py-2 text-sm text-center text-emerald-600 font-semibold">{item.soKhoi.toFixed(3)}</td>
                        <td className="px-3 py-2 text-sm text-right text-emerald-700 font-bold">{formatCurrency(item.doanhSo)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100">
                    <tr className="font-bold">
                      <td colSpan="2" className="px-3 py-3 text-sm text-center text-gray-900">TỔNG TOP 10</td>
                      <td className="px-3 py-3 text-sm text-center text-emerald-700">
                        {formatNumber(doanhSoData.reduce((sum, item) => sum + item.soKien, 0))}
                      </td>
                      <td className="px-3 py-3 text-sm text-center text-emerald-700">
                        {doanhSoData.reduce((sum, item) => sum + item.soKhoi, 0).toFixed(3)}
                      </td>
                      <td className="px-3 py-3 text-sm text-right text-emerald-700">
                        {formatCurrency(doanhSoData.reduce((sum, item) => sum + item.doanhSo, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Category Report Tab */}
        {activeTab === 'category' && (
          <>
            {/* Year Filter for Category Report */}
            <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-64">
                  <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Chọn năm:
                  </label>
                  <select
                    value={filterYear}
                    onChange={(e) => setFilterYear(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm bg-white"
                  >
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year}>
                        Năm {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Category Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-emerald-700">TỔNG DOANH SỐ NĂM {filterYear}</h3>
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="text-2xl font-bold text-emerald-900">
                  {formatCurrency(Object.values(categoryTotal).reduce((sum, month) => sum + month.doanhSo, 0))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-indigo-700">TỔNG KHỐI LƯỢNG NĂM {filterYear}</h3>
                  <Archive className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="text-2xl font-bold text-indigo-900">
                  {Object.values(categoryTotal).reduce((sum, month) => sum + month.soKhoi, 0).toFixed(3)} m³
                </div>
              </div>
            </div>

            {/* Category Table - 12 Months */}
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
              <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Báo Cáo Xuất Hàng Theo Loại - Năm {filterYear}
              </h2>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gradient-to-r from-yellow-100 to-yellow-200">
                    <tr>
                      <th rowSpan="2" className="px-2 py-2 text-center font-bold text-gray-800 border border-gray-300 bg-yellow-100">
                        Quy cách
                      </th>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                        <th key={month} colSpan="3" className="px-2 py-2 text-center font-bold text-gray-800 border border-gray-300 bg-orange-400">
                          THÁNG {month}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                        <React.Fragment key={month}>
                          <th className="px-2 py-2 text-center font-bold text-gray-800 border border-gray-300 bg-gray-100">M3</th>
                          <th className="px-2 py-2 text-center font-bold text-gray-800 border border-gray-300 bg-gray-100">Tỷ lệ %</th>
                          <th className="px-2 py-2 text-center font-bold text-gray-800 border border-gray-300 bg-yellow-200">%</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {categoryData.map((item, index) => {
                      // Check if this is the first row of a DAY group
                      const dayMatch = item.category.match(/^(\d+)/);
                      const currentDay = dayMatch ? dayMatch[1] : 'other';
                      const prevItem = index > 0 ? categoryData[index - 1] : null;
                      const prevDayMatch = prevItem ? prevItem.category.match(/^(\d+)/) : null;
                      const prevDay = prevDayMatch ? prevDayMatch[1] : 'other';
                      const isFirstInGroup = currentDay !== prevDay;

                      // Calculate rowspan for this DAY group
                      const rowSpanCount = categoryData.filter(c => {
                        const m = c.category.match(/^(\d+)/);
                        return m && m[1] === currentDay;
                      }).length;

                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-2 py-2 text-left font-medium text-gray-900 border border-gray-300">
                            {item.category}
                          </td>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                            <React.Fragment key={month}>
                              <td className="px-2 py-1 text-center text-emerald-600 font-semibold border border-gray-300 bg-pink-50">
                                {item.months[month].soKhoi > 0 ? formatNumber(item.months[month].soKhoi) : ''}
                              </td>
                              <td className="px-2 py-1 text-center text-blue-600 font-semibold border border-gray-300 bg-pink-50">
                                {item.months[month].tyLe > 0 ? `${item.months[month].tyLe.toFixed(1)}%` : ''}
                              </td>
                              {isFirstInGroup ? (
                                <td
                                  rowSpan={rowSpanCount}
                                  className="px-2 py-1 text-center text-purple-700 font-bold border border-gray-300 bg-yellow-100"
                                >
                                  {item.months[month].tyLeNhom > 0 ? `${item.months[month].tyLeNhom.toFixed(1)}%` : ''}
                                </td>
                              ) : null}
                            </React.Fragment>
                          ))}
                        </tr>
                      );
                    })}

                    {/* Total Row */}
                    <tr className="bg-yellow-100 font-bold">
                      <td className="px-2 py-2 text-center text-gray-900 border border-gray-300">Cộng</td>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                        <React.Fragment key={month}>
                          <td className="px-2 py-2 text-center text-emerald-700 border border-gray-300">
                            {categoryTotal[month] ? formatNumber(categoryTotal[month].soKhoi) : '0'}
                          </td>
                          <td className="px-2 py-2 text-center text-blue-700 border border-gray-300">
                            100%
                          </td>
                          <td className="px-2 py-2 text-center text-purple-700 border border-gray-300">
                            100%
                          </td>
                        </React.Fragment>
                      ))}
                    </tr>

                    {/* Revenue Row */}
                    <tr className="bg-orange-100 font-bold">
                      <td className="px-2 py-2 text-left text-gray-900 border border-gray-300">Doanh thu (tr)</td>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                        <td key={month} colSpan="3" className="px-2 py-2 text-center text-emerald-700 border border-gray-300">
                          {categoryTotal[month] ? formatNumber(categoryTotal[month].doanhSo / 1000000) : '0'}
                        </td>
                      ))}
                    </tr>

                    {/* Average Price Row */}
                    <tr className="bg-orange-100 font-bold">
                      <td className="px-2 py-2 text-left text-gray-900 border border-gray-300">TB VNĐ(tr)/ M3</td>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                        <td key={month} colSpan="3" className="px-2 py-2 text-center text-purple-700 border border-gray-300">
                          {categoryTotal[month] && categoryTotal[month].soKhoi > 0
                            ? (categoryTotal[month].doanhSo / categoryTotal[month].soKhoi / 1000000).toFixed(2)
                            : '0'}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default BaoCaoDoanhSo;
